import { FormsyInput } from '@thanx/uikit/input'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import CalendarSchedule from 'components/Icons/CalendarSchedule'
import Lightning from 'components/Icons/Lightning'
import InfoBox from 'components/InfoBox'
import PopoverContent from 'components/PopoverContent'
import { buildTranslate } from 'locales'
import React, { useCallback } from 'react'
import { Button } from 'react-bootstrap'
import HowRoundsWorkModal from './HowRoundsWorkModal'
import SelectableCard from './SelectableCard'

const t = buildTranslate(
  'redeem_manager.builder.steps.configuration.multi_use_rewards_card'
)
const vt = buildTranslate('validations')

const MIN_ISSUANCES = 2
const MIN_ISSUANCES_PER_WINDOW = 1

interface CustomFormProps {
  limitType: 'set_number' | 'unlimited'
  maxIssuancesPerWindow?: number
  windowDuration?: number
  maxIssuances?: number
  activeWindowDuration?: number
  showMaxIssuances: boolean
  isBonusPoints: boolean
  isMultipleUse: boolean
  estimatedMaxValue?: number | null
  customSummaryText: string | null
  onBackToPresets: () => void
  onLimitTypeChange: (type: 'set_number' | 'unlimited') => void
  onToggleMaxIssuances: () => void
}

const CustomForm: React.FC<CustomFormProps> = ({
  limitType,
  maxIssuancesPerWindow,
  windowDuration,
  maxIssuances,
  activeWindowDuration,
  showMaxIssuances,
  isBonusPoints,
  isMultipleUse,
  estimatedMaxValue,
  customSummaryText,
  onBackToPresets,
  onLimitTypeChange,
  onToggleMaxIssuances,
}) => {
  const [css] = useStyletron()
  const [showHowItWorksModal, setShowHowItWorksModal] = React.useState(false)

  const isUnlimited = limitType === 'unlimited'
  const scheduleExpiryHours = windowDuration ? windowDuration * 24 : 0

  const handleReissueClick = useCallback(
    () => onLimitTypeChange('unlimited'),
    [onLimitTypeChange]
  )
  const handleScheduleClick = useCallback(
    () => onLimitTypeChange('set_number'),
    [onLimitTypeChange]
  )
  const handleOpenModal = useCallback(() => setShowHowItWorksModal(true), [])
  const handleCloseModal = useCallback(() => setShowHowItWorksModal(false), [])

  return (
    <div className="mt-s">
      <div className="mb-s">
        <Button
          bsStyle="link"
          aria-label={t('back_to_presets')}
          className={`p-0 ${css({
            ':focus': {
              textDecoration: 'none',
            },
          })}`}
          onClick={onBackToPresets}
        >
          ← {t('back_to_presets')}
        </Button>
      </div>

      <div className="d-flex mb-m" style={{ gap: '8px' }}>
        {!isBonusPoints && (
          <SelectableCard
            isSelected={isUnlimited}
            onClick={handleReissueClick}
            ariaLabel={`${t('reissue_immediately_title')}: ${t(
              'reissue_immediately_description'
            )}`}
            icon={<Lightning />}
            title={t('reissue_immediately_title')}
            description={t('reissue_immediately_description')}
          />
        )}
        <SelectableCard
          isSelected={!isUnlimited}
          onClick={handleScheduleClick}
          ariaLabel={`${t('issue_on_schedule_title')}: ${t(
            'issue_on_schedule_description'
          )}`}
          icon={<CalendarSchedule />}
          title={t('issue_on_schedule_title')}
          description={t('issue_on_schedule_description')}
        />
      </div>

      {/* Schedule fields — always mounted, hidden when unlimited */}
      <div style={{ display: isUnlimited ? 'none' : undefined }}>
        <div className="d-flex justify-content-between align-items-center mb-xs">
          <Text.BodyText4 bold>{t('issuance_schedule_label')}</Text.BodyText4>
          <Button
            bsStyle="link"
            className={`p-0 ${css({
              fontSize: '14px',
              ':focus': {
                textDecoration: 'none',
              },
            })}`}
            aria-label={t('how_it_works')}
            onClick={handleOpenModal}
          >
            {t('how_it_works')}
          </Button>
        </div>
        <div className="d-flex align-items-top flex-nowrap mb-xs">
          <div className="d-flex align-items-center" style={{ height: '48px' }}>
            <Text.BodyText4 color="grey70" className="mr-xs">
              {t('issuance_schedule_issue')}
            </Text.BodyText4>
          </div>
          <FormsyInput
            name="multiUseRewards.maxIssuancesPerWindow"
            type="number"
            value={maxIssuancesPerWindow}
            className="width-50"
            endEnhancer={
              <span className="body-text-4 grey-70">
                {t('issuance_schedule_rewards')}
              </span>
            }
            validations={{
              isRequired: isMultipleUse && !isUnlimited,
              isInt: true,
              greaterThanOrEqual: MIN_ISSUANCES_PER_WINDOW,
            }}
            validationErrors={{
              isRequired: vt('is_required'),
              isInt: vt('is_int'),
              greaterThanOrEqual: vt('greater_than_or_equal', {
                value: MIN_ISSUANCES_PER_WINDOW,
              }),
            }}
          />
          <div className="d-flex align-items-center" style={{ height: '48px' }}>
            <Text.BodyText4 color="grey70" className="mx-xs">
              {t('issuance_schedule_every')}
            </Text.BodyText4>
          </div>
          <FormsyInput
            name="multiUseRewards.windowDuration"
            type="number"
            value={windowDuration}
            className="width-50"
            endEnhancer={
              <span className="body-text-4 grey-70">
                {t('issuance_schedule_days')}
              </span>
            }
            validations={{
              isRequired: isMultipleUse && !isUnlimited,
              isInt: true,
              greaterThanOrEqual: 1,
            }}
            validationErrors={{
              isRequired: vt('is_required'),
              isInt: vt('is_int'),
              greaterThanOrEqual: vt('greater_than_or_equal', {
                value: 1,
              }),
            }}
          />
        </div>

        {scheduleExpiryHours > 0 && maxIssuancesPerWindow && (
          <div
            className="mb-m"
            style={{
              borderLeft: '3px solid #0073BF',
              backgroundColor: '#F5F8FA',
              padding: '12px 16px',
              borderRadius: '2px',
            }}
          >
            <Text.BodyText4 color="grey70">
              {t('schedule_expiry_info', {
                hours: scheduleExpiryHours,
              })}
            </Text.BodyText4>
          </div>
        )}
      </div>

      <div className="mb-m">
        <Text.BodyText4 bold className="mb-xs">
          {t('max_rewards_label')}
        </Text.BodyText4>
        {/* maxIssuances — always mounted, hidden when set_number and no value */}
        <div style={{ display: showMaxIssuances ? undefined : 'none' }}>
          <FormsyInput
            name="multiUseRewards.maxIssuances"
            type="number"
            value={maxIssuances}
            endEnhancer={
              <span className="body-text-4 grey-70">{t('times')}</span>
            }
            validations={{
              isRequired: isMultipleUse && isUnlimited,
              isInt: true,
              greaterThanOrEqual: MIN_ISSUANCES,
            }}
            validationErrors={{
              isRequired: vt('is_required'),
              isInt: vt('is_int'),
              greaterThanOrEqual: t('max_redemptions_validation'),
            }}
          />
        </div>
        {!isUnlimited && (
          <div className="mt-xs">
            <label
              className="d-flex align-items-center"
              style={{ cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={maxIssuances == null}
                onChange={onToggleMaxIssuances}
                className="mr-xs"
                style={{ cursor: 'pointer' }}
              />
              <Text.BodyText4 color="grey70">
                {t('unlimited_rewards_label')}
              </Text.BodyText4>
            </label>
          </div>
        )}
      </div>

      <div className="mb-m">
        <div className="d-flex align-items-center mb-xs">
          <Text.BodyText4 bold className="mr-xxs">
            {t('program_duration_label')}
          </Text.BodyText4>
          <PopoverContent
            sections={[
              {
                content: t('program_duration_tooltip'),
              },
            ]}
          />
        </div>
        <FormsyInput
          name="multiUseRewards.activeWindowDuration"
          type="number"
          value={activeWindowDuration}
          endEnhancer={<span className="body-text-4 grey-70">{t('days')}</span>}
          validations={{
            isRequired: isMultipleUse,
            isInt: true,
            greaterThanOrEqual: 1,
          }}
          validationErrors={{
            isRequired: vt('is_required'),
            isInt: vt('is_int'),
            greaterThanOrEqual: vt('greater_than_or_equal', {
              value: 1,
            }),
          }}
        />
      </div>

      {customSummaryText && (
        <div
          className="mb-m"
          style={{
            borderLeft: '3px solid #0073BF',
            backgroundColor: '#F5F8FA',
            padding: '12px 16px',
            borderRadius: '2px',
          }}
        >
          <Text.BodyText4 color="grey70">{customSummaryText}</Text.BodyText4>
        </div>
      )}

      {estimatedMaxValue != null && (
        <InfoBox
          type="info"
          className="mb-m"
          content={
            <div>
              <Text.BodyText4 color="grey70">
                {t('estimated_max_value')}
              </Text.BodyText4>
              <Text.BodyText4 bold color="grey70" className="mr-xxs">
                {estimatedMaxValue}
              </Text.BodyText4>
              <PopoverContent
                sections={[
                  {
                    content: t('estimated_max_info'),
                  },
                ]}
              />
            </div>
          }
        />
      )}

      <HowRoundsWorkModal
        show={showHowItWorksModal}
        onClose={handleCloseModal}
      />
    </div>
  )
}

export default CustomForm
